import React from 'react';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Scatter } from 'react-chartjs-2';
import { formatMoney } from '../../utils/formatters';

ChartJS.register(LinearScale, PointElement, Tooltip, Legend);

/* Chart.js рисует подписи легенды обычным текстом, а не JSX — SVG-иконку сюда
   не вставить, поэтому просто убираем эмодзи из строк. Значок для тех же
   квадрантов см. в BCG_CARDS (KitchenPage.jsx). */
const QUADRANTS = [
  { id: 'stars', name: 'Звёзды', color: '#10b981', pointStyle: 'circle' },
  { id: 'cows', name: 'Коровы', color: '#6366f1', pointStyle: 'triangle' },
  { id: 'children', name: 'Дети', color: '#f59e0b', pointStyle: 'rect' },
  { id: 'dogs', name: 'Собаки', color: '#94a3b8', pointStyle: 'rectRot' }
];

/* Четыре списка по восемь позиций показывают лидеров квадрантов, но не
   расстановку меню целиком — облако точек «выручка × маржа» показывает.
   Линии средних рисуем отдельным плагином: это и есть границы квадрантов. */
const averagesPlugin = {
  id: 'bcgAverages',
  afterDatasetsDraw(chart, args, opts) {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea || !opts?.avgRevenue) return;
    const x = scales.x.getPixelForValue(opts.avgRevenue);
    const y = scales.y.getPixelForValue(opts.avgMargin);
    ctx.save();
    ctx.strokeStyle = 'rgba(100, 116, 139, 0.45)';
    ctx.setLineDash([5, 5]);
    ctx.lineWidth = 1;
    if (x >= chartArea.left && x <= chartArea.right) {
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
    }
    if (y >= chartArea.top && y <= chartArea.bottom) {
      ctx.beginPath();
      ctx.moveTo(chartArea.left, y);
      ctx.lineTo(chartArea.right, y);
      ctx.stroke();
    }
    ctx.restore();
  }
};

const BCGScatter = ({ menuAnalysis }) => {
  const points = menuAnalysis?.points || [];
  if (!points.length) {
    return <div className="kt-empty">Недостаточно данных для матрицы</div>;
  }

  const data = {
    datasets: QUADRANTS.map((quadrant) => ({
      label: quadrant.name,
      data: points
        .filter((p) => p.quadrant === quadrant.id)
        .map((p) => ({ x: p.revenue, y: p.margin_percent, dish: p })),
      backgroundColor: quadrant.color,
      borderColor: quadrant.color,
      pointStyle: quadrant.pointStyle,
      pointRadius: 5,
      pointHoverRadius: 8
    }))
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8, padding: 16 } },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const dish = ctx.raw.dish;
            return [
              dish.name,
              `${dish.group} • ${dish.type === 'kitchen' ? 'кухня' : 'бар'}`,
              `Выручка: ${formatMoney(dish.revenue)}`,
              `Маржа: ${dish.margin_percent}% • продано ${dish.amount} шт.`
            ];
          }
        }
      },
      bcgAverages: {
        avgRevenue: menuAnalysis?.avg_revenue || 0,
        avgMargin: menuAnalysis?.avg_margin || 0
      }
    },
    scales: {
      x: {
        title: { display: true, text: 'Выручка за период, ₽' },
        ticks: { callback: (value) => `${Math.round(value / 1000)} т` },
        grid: { color: 'rgba(148, 163, 184, 0.18)' }
      },
      y: {
        title: { display: true, text: 'Маржа, %' },
        ticks: { callback: (value) => `${value}%` },
        grid: { color: 'rgba(148, 163, 184, 0.18)' }
      }
    }
  };

  return (
    <div
      className="kt-chart kt-chart-tall"
      role="img"
      aria-label="Матрица BCG по блюдам: выручка к марже, разбивка на звёзды, коровы, дети и собаки"
    >
      <Scatter data={data} options={options} plugins={[averagesPlugin]} />
    </div>
  );
};

export default BCGScatter;
